// src/components/create-project/index.tsx
import { FC, useEffect, useState } from 'react';
import { FolderPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProjectProvider } from './context/ProjectContext';
import { useProjects } from './hooks/useProjects';
import { ProjectListModal } from './ProjectListModal';
import { Project } from './types/project';

const STORAGE_KEY = {
  PROJECT_ID: 'selectedProjectId',
  PROJECT_NAME: 'selectedProjectName',
  PROJECT_USE_CASES: 'selectedProjectUseCases'
};

interface ProjectSelectorProps {
  selectedProjectId?: string;
  onSelectProject?: (projectId: string) => void;
}

const ProjectSelectorContent: FC<ProjectSelectorProps> = ({
  selectedProjectId,
  onSelectProject,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { selectedProject, selectProject, fetchProjectById } = useProjects();

  useEffect(() => {
    const storedProjectId = sessionStorage.getItem(STORAGE_KEY.PROJECT_ID);
    const storedProjectName = sessionStorage.getItem(STORAGE_KEY.PROJECT_NAME);
    const storedProjectUseCases = sessionStorage.getItem(STORAGE_KEY.PROJECT_USE_CASES);
    
    if (storedProjectId && storedProjectName && storedProjectUseCases) {
      selectProject({
        projectId: storedProjectId,
        name: storedProjectName,
        useCases: storedProjectUseCases ? JSON.parse(storedProjectUseCases) : []
      } as Project);
      fetchProjectById(storedProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProjectSelect = async (project: Project) => {
    sessionStorage.setItem(STORAGE_KEY.PROJECT_ID, project.projectId);
    sessionStorage.setItem(STORAGE_KEY.PROJECT_NAME, project.name);
    sessionStorage.setItem(STORAGE_KEY.PROJECT_USE_CASES, JSON.stringify(project.useCases));
    await selectProject(project);
    onSelectProject?.(project.projectId);
    setIsModalOpen(false);
  };

  return (
    <>
        <Button 
          variant="ghost" 
          onClick={() => setIsModalOpen(true)} 
          className="px-4 text-primary-500 border border-primary-500 hover:bg-primary-500 hover:text-white flex items-center gap-2 w-full sm:w-fit"
        >
          <FolderPlus className="w-5 h-5" />
          <span className="font-medium">
            {selectedProject ? 'Create or Switch Project' : 'Select a Project'}
          </span>
        </Button>
      <ProjectListModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelectProject={handleProjectSelect}
      />
    </>
  );
};

export const ProjectSelector: FC<ProjectSelectorProps> = (props) => {
  return (
    <ProjectProvider>
      <ProjectSelectorContent {...props} />
    </ProjectProvider>
  );
};

export * from './types/project';
export * from './hooks/useProjects';
export { ProjectProvider } from './context/ProjectContext';